import { useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';
import { formatTimecode, formatDurationWords } from '../../domain/time';

/**
 * Create-excerpt confirm dialog.
 *
 * Fire-and-forget (Background Jobs UX): shows the range, its length, the
 * transcription estimate and the cut mode; "Create excerpt" submits the
 * `excerpt_media` job and closes. Progress lives in the BackgroundJobsBanner
 * and a toast fires on completion with "View excerpt" / "Download"
 * (jobRegistry). The new document is transcribed in full on its own.
 */
export default function ExcerptCreateModal({ caseId, documentId, excerpt, isVideo, onClose, onSubmitted }) {
  const { submitJob } = useBackgroundJobs();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const { range, lengthSeconds, estimatedCost, cutMode } = excerpt;

  async function handleCreate() {
    if (!range) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await submitJob(
        'excerpt_media',
        api.post(`/cases/${caseId}/documents/${documentId}/excerpts`, {
          time_start: range.start,
          time_end: range.end,
          mode: isVideo ? cutMode : 'exact',
        }),
      );
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      setErrorMsg(e.message || 'Could not start the excerpt.');
      setSubmitting(false);
    }
  }

  return createPortal((
    <div className="np-export-overlay" onClick={submitting ? undefined : onClose}>
      <div className="np-export-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="np-export-head">
          <h3>Create excerpt</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close" disabled={submitting}>✕</button>
        </div>
        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <div style={{ fontSize: 13 }}>
            Cut <strong>{formatTimecode(range?.start)}</strong> – <strong>{formatTimecode(range?.end)}</strong>
            {' '}({formatDurationWords(lengthSeconds)}) into its own document.
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            It will be transcribed in full (about ${estimatedCost.toFixed(2)}) and reach Deep search on its own.
            The original is never modified. Audio stays inside the Google boundary.
          </div>
          {isVideo && (
            <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
              {cutMode === 'exact'
                ? 'Exact to the frame — the video is re-encoded, so this takes a minute or two.'
                : 'Fast cut — seconds, but the excerpt may start a second or two early (keyframe).'}
            </div>
          )}
          {errorMsg && (
            <div className="bulk-popover-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
        </div>
        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={submitting || !excerpt.valid}>
            {submitting ? 'Starting…' : 'Create excerpt'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
